import React, { useEffect, useLayoutEffect, useMemo } from "react";
import { useSearch } from "../../../contexts/SearchContext";
import {
  countFlightPassengersFromRoomInfo,
  createEmptyFlightPassengerSlot,
  DEFAULT_FLIGHT_PASSENGER_NATIONALITY,
} from "../../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import {
  normalizePassengersNationality,
  shouldRequirePassportOnAgentFlightForm,
  validateFlightPassengerPax,
} from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/flightPassengersFormShared";
import {
  bookingContactFromPassengers,
  bookingPassengersToScreenPassengers,
  buildLivePassengersFormModel,
  LIVE_PASSPORT_REQUIRED_KEYS,
  patchBookingContact,
  patchBookingPassengerField,
} from "../bridge/liveStageModels";
import { useLiveFlightStage } from "../bridge/useLiveFlightStage";
import PassengersScreen from "./PassengersScreen";

/**
 * Thin live host. PassengersScreen is the preview component.
 * Values stay on bookingFlow.passengers.
 */
export default function PassengersLive() {
  const { booking, presentation, onBack } = useLiveFlightStage();
  const { roomInfo } = useSearch();
  const {
    passengers,
    updatePassengers,
    setPassengersStepValid,
    selectedFlightOffer,
    currentStep,
    nextStep,
  } = booking;

  const requirePassport = shouldRequirePassportOnAgentFlightForm(selectedFlightOffer);
  const expected = useMemo(
    () => countFlightPassengersFromRoomInfo(roomInfo),
    [roomInfo]
  );
  const isActive = currentStep === "passengers";

  useEffect(() => {
    if (!isActive) return;
    if (passengers.length === expected) return;
    const next = Array.from({ length: expected }, (_, i) => {
      if (passengers[i]) {
        const p = { ...passengers[i], slotIndex: i };
        if (!String(p.nationality || "").trim()) {
          p.nationality = DEFAULT_FLIGHT_PASSENGER_NATIONALITY;
        }
        if (p.cell === undefined) p.cell = "";
        if (p.email === undefined) p.email = "";
        return p;
      }
      return createEmptyFlightPassengerSlot(i);
    });
    updatePassengers(next);
  }, [isActive, expected, passengers, updatePassengers]);

  useEffect(() => {
    if (!isActive) return;
    if (passengers.length !== expected || expected < 1) return;
    const { next, changed } = normalizePassengersNationality(passengers);
    if (changed) updatePassengers(next);
  }, [isActive, expected, passengers, updatePassengers]);

  const allValid = useMemo(() => {
    if (passengers.length !== expected || expected < 1) return false;
    return passengers.every((p, i) =>
      validateFlightPassengerPax(p, i, { requirePassport })
    );
  }, [passengers, expected, requirePassport]);

  useLayoutEffect(() => {
    if (!isActive) return;
    setPassengersStepValid(allValid);
  }, [allValid, isActive, setPassengersStepValid]);

  const formModel = useMemo(
    () => buildLivePassengersFormModel(passengers, roomInfo),
    [passengers, roomInfo]
  );
  const screenPassengers = useMemo(
    () => bookingPassengersToScreenPassengers(passengers, roomInfo),
    [passengers, roomInfo]
  );
  const contact = useMemo(
    () => bookingContactFromPassengers(passengers),
    [passengers]
  );

  return (
    <PassengersScreen
      offer={presentation.offer}
      search={presentation.search}
      formModel={formModel}
      passengers={screenPassengers}
      contact={contact}
      requirePassport={requirePassport}
      passportRequiredKeys={LIVE_PASSPORT_REQUIRED_KEYS}
      continueDisabled={!allValid}
      onPassengerFieldChange={(id, key, value) => {
        updatePassengers(patchBookingPassengerField(passengers, id, key, value));
      }}
      onContactFieldChange={(key, value) => {
        updatePassengers(patchBookingContact(passengers, key, value));
      }}
      onContinue={() => {
        if (allValid) nextStep();
      }}
      onBack={onBack}
    />
  );
}
